import { useMemo, useRef } from 'react';
import { Lipsync } from 'wawa-lipsync';

export const useAvatarAudio = () => {
  // Create persistent AudioContext
  const sourceRef = useRef<AudioNode | null>(null);


  // Create a single persistent Lipsync instance
  const lipsyncRef = useMemo(() => {
    const lipsync = new Lipsync({
      fftSize: 1024,
      historySize: 8 // Reducido de 128 a 8 para respuesta instantánea
    });
    
    return lipsync;
  }, []);

  const playFromBase64 = async (base64String: string) => {
  const ctx = (lipsyncRef as any).audioContext;

  try {
    // 1. Limpiar el string si viene con el prefijo "data:audio/..."
    const base64Data = base64String.split(',')[1] || base64String;

    // 2. Convertir String Base64 a ArrayBuffer
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const arrayBuffer = bytes.buffer;

    // 3. Decodificar el AudioBuffer
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

    // 4. Crear y configurar el nodo de fuente
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;

    // 5. CONEXIÓN PARA LIP-SYNC
    // Conectamos la fuente al Analyser de la librería
    source.connect((lipsyncRef as any).analyser);
    
    // Conectamos el Analyser al destino (altavoces)
    (lipsyncRef as any).analyser.connect(ctx.destination);

    // 6. Iniciar reproducción
    if (ctx.state === 'suspended') await ctx.resume();
    source.start(0);
    
    console.log('✅ Lip-sync activo con fuente de string base64');

  } catch (error) {
    console.error('❌ Error procesando el string de audio:', error);
  }
};

  const playFromBytes = async (byteArray: ArrayBuffer) => {
  const ctx = (lipsyncRef as any).audioContext;

  // 1. Asegurar que el contexto esté activo
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }

  try {
    // 2. Decodificar los bytes a un AudioBuffer
    const audioBuffer = await ctx.decodeAudioData(byteArray);

    // 3. Crear el nodo de fuente (AudioBufferSourceNode)
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;

    // 4. CONEXIÓN CRÍTICA: Conectar a Analyser para Lip-Sync
    // source -> analyser -> destination
    source.connect((lipsyncRef as any).analyser);
    
    // Conectar el analyser a la salida para poder oírlo
    (lipsyncRef as any).analyser.connect(ctx.destination);

    // 5. Iniciar reproducción
    source.start(0);
    console.log('✅ Reproduciendo desde bytes y analizando para Lip-Sync');

    // Opcional: Guardar referencia para detenerlo si es necesario
    sourceRef.current = source; 

  } catch (error) {
    console.error('❌ Error al decodificar o reproducir audio:', error);
  }
};

  const connectAudioElement = (audioElement: HTMLAudioElement) => {
    try {
      const ctx = (lipsyncRef as any).audioContext;
      console.log('Context state:', ctx.state);

      // 1. Resume AudioContext on user interaction
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => console.log('AudioContext resumed'));
      }

      // 2. IMPORTANT: MediaElementSource can only be created ONCE per element.
      // We check a custom flag on the element to avoid "node already connected" errors.
      if ((audioElement as any)._isSourceConnected) {
        console.log('Audio element source already connected');
        return;
      }

      const source = ctx.createMediaElementSource(audioElement);
      sourceRef.current = source;

      // 4. Connect source to analyzer
      source.connect((lipsyncRef as any).analyser);

      // 5. !!! CRITICAL: Connect analyzer to destination to hear audio !!!
      (lipsyncRef as any).analyser.connect(ctx.destination);
      
      (audioElement as any)._isSourceConnected = true;

      console.log('✅ Audio connected and routed to speakers');
    } catch (error) {
      console.error('❌ Error connecting audio element:', error);
    }
  };
  // Connect a MediaStream directly (better for WebRTC)
  const connectMediaStream = (stream: MediaStream) => {
    try {
      //const ctx = setupAudioContext();
      console.log(stream)
      const ctx = (lipsyncRef as any).audioContext;
      console.log(ctx)
      // Resume AudioContext if suspended
      if (ctx.state === 'suspended') {
        ctx.resume().catch((e:any) => console.warn('AudioContext resume failed:', e));
      }

      // Create source directly from MediaStream
      const source = ctx.createMediaStreamSource(stream);

      sourceRef.current = source;

      // Connect source to analyser
      source.connect((lipsyncRef as any).analyser);

      // Conectar a un nodo de ganancia en silencio para asegurar que el AudioContext 
      // procese el flujo y el analizador obtenga datos en tiempo real.
      const silentGain = ctx.createGain();
      silentGain.gain.value = 0;
      (lipsyncRef as any).analyser.connect(silentGain);
      silentGain.connect(ctx.destination);

      console.log('✅ MediaStream connected to Web Audio context for analysis');
      //return analyser;
    } catch (error) {
      console.error('❌ Error connecting MediaStream:', error);
      return null;
    }
  };



  return {
    lipsync: lipsyncRef,
    connectMediaStream,
    connectAudioElement,
    playFromBytes,
    playFromBase64,
    analyser: (lipsyncRef as any).analyser,
    audioContext: (lipsyncRef as any).audioContext
  };
};